import React, { useState, useEffect } from 'react';
import { orEnCours as orApi } from '../services/api';
import './ORENCours.css';

import { texteTheme } from '../utils/theme';
const STATUTS = [
  { value: 'en_cours',       label: 'En cours',        color: '#2563eb' },
  { value: 'attente_pieces', label: 'Attente pièces',   color: '#f59e0b' },
  { value: 'contacte',       label: 'Client contacté',  color: '#8b5cf6' },
  { value: 'bloquant',       label: 'Point bloquant',   color: '#ef4444' },
  { value: 'rdv_pris',       label: 'RDV pris',         color: '#10b981' },
  { value: 'surveiller',     label: 'À surveiller',     color: '#f97316' },
];

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-FR');
}
function formatMontant(m) {
  if (m == null) return '—';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(m);
}

/**
 * Modal OR en cours réutilisable.
 * Props :
 *   orId          — id de l'OR à charger
 *   user          — user courant (pour isAdmin)
 *   onClose       — fermer la modale
 *   onStatutChange(id, statut, action) — callback optionnel appelé après sauvegarde
 */
export default function ORModal({ orId, user, onClose, onStatutChange }) {
  const isAdmin = user.role === 'super-admin' || user.role === 'admin';

  const [orDetail,       setOrDetail]       = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [commentaires,   setCommentaires]   = useState([]);
  const [editStatut,     setEditStatut]     = useState('en_cours');
  const [editDateRevision, setEditDateRevision] = useState('');
  const [saving,         setSaving]         = useState(false);
  const [newComment,     setNewComment]     = useState('');
  const [savingComment,  setSavingComment]  = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [detailRes, comRes] = await Promise.all([
          orApi.getById(orId),
          orApi.getCommentaires(orId),
        ]);
        const d = detailRes.data;
        setOrDetail(d);
        setEditStatut(d.statut || 'en_cours');
        setEditDateRevision(d.date_revision ? d.date_revision.split('T')[0] : '');
        setCommentaires(comRes.data.commentaires || []);
      } catch (err) {
        console.error('[ORModal] Erreur chargement:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [orId]);

  const handleSaveStatut = async () => {
    setSaving(true);
    try {
      await orApi.updateStatut(orId, { statut: editStatut, date_revision: editDateRevision || null });
      setOrDetail(prev => ({ ...prev, statut: editStatut, date_revision: editDateRevision || null }));
      onStatutChange && onStatutChange(orId, editStatut);
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    setSavingComment(true);
    try {
      await orApi.addCommentaire(orId, newComment.trim());
      setNewComment('');
      const r = await orApi.getCommentaires(orId);
      setCommentaires(r.data.commentaires || []);
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    } finally {
      setSavingComment(false);
    }
  };

  const handleDeleteComment = async (cid) => {
    if (!window.confirm('Supprimer ce commentaire ?')) return;
    try {
      await orApi.deleteCommentaire(orId, cid);
      setCommentaires(prev => prev.filter(c => c.id !== cid));
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  return (
    <div className="orc-modal-overlay">
      <div className="orc-modal" onClick={e => e.stopPropagation()}>
        <div className="orc-modal-header">
          <div className="orc-modal-title">
            {orDetail ? (
              <>
                <span className="orc-modal-numor">OR {orDetail.num_or || orDetail.id_or}</span>
                <span className="orc-modal-client">{orDetail.nom_client}</span>
              </>
            ) : (
              <span className="orc-modal-numor">OR #{orId}</span>
            )}
          </div>
          <button className="orc-modal-close" onClick={onClose}>✕</button>
        </div>

        {loading ? (
          <div className="orc-modal-loading">Chargement…</div>
        ) : orDetail ? (
          <div className="orc-modal-body">
            {/* ── Infos OR ── */}
            <div className="orc-modal-grid">
              <div className="orc-modal-field">
                <div className="orc-modal-field-label">Immatriculation</div>
                <div className="orc-modal-field-val orc-immat">{orDetail.immat || '—'}</div>
              </div>
              <div className="orc-modal-field">
                <div className="orc-modal-field-label">Châssis</div>
                <div className="orc-modal-field-val">{orDetail.chassis || '—'}</div>
              </div>
              <div className="orc-modal-field">
                <div className="orc-modal-field-label">Marque</div>
                <div className="orc-modal-field-val">{orDetail.marque || '—'}</div>
              </div>
              <div className="orc-modal-field">
                <div className="orc-modal-field-label">Réceptionnaire</div>
                <div className="orc-modal-field-val">{orDetail.receptionnaire || '—'}</div>
              </div>
              <div className="orc-modal-field">
                <div className="orc-modal-field-label">Société</div>
                <div className="orc-modal-field-val">{orDetail.societe || '—'}</div>
              </div>
              <div className="orc-modal-field">
                <div className="orc-modal-field-label">CIGA</div>
                <div className="orc-modal-field-val">{orDetail.ciga || '—'}</div>
              </div>
              <div className="orc-modal-field">
                <div className="orc-modal-field-label">Date création</div>
                <div className="orc-modal-field-val">{formatDate(orDetail.date_creation)}</div>
              </div>
              <div className="orc-modal-field">
                <div className="orc-modal-field-label">Début travaux</div>
                <div className="orc-modal-field-val">{formatDate(orDetail.date_debut_travaux)}</div>
              </div>
              <div className="orc-modal-field">
                <div className="orc-modal-field-label">Montant HT</div>
                <div className="orc-modal-field-val orc-montant">{formatMontant(orDetail.montant_ht)}</div>
              </div>
            </div>

            {/* ── Lignes opérations ── */}
            {orDetail.lignes_json && orDetail.lignes_json.length > 0 && (
              <div className="orc-modal-section">
                <div className="orc-modal-section-title">Lignes d'opérations ({orDetail.nb_lignes})</div>
                <div className="orc-lignes-wrap">
                  <table className="orc-lignes-table">
                    <thead>
                      <tr>
                        <th>Opération</th>
                        <th>Type</th>
                        <th>Qté</th>
                        <th>PU HT</th>
                        <th>Total HT</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orDetail.lignes_json.map((ligne, i) => (
                        <tr key={i}>
                          <td>{ligne.operation || '—'}</td>
                          <td>{ligne.type_pmtk || '—'}</td>
                          <td className="orc-center">{ligne.qte || '—'}</td>
                          <td className="orc-montant">{ligne.pu ? formatMontant(ligne.pu) : '—'}</td>
                          <td className="orc-montant">{ligne.montant_ht ? formatMontant(ligne.montant_ht) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ── Statut & Date révision ── */}
            <div className="orc-modal-section">
              <div className="orc-modal-section-title">Suivi réception</div>
              <div className="orc-statut-form">
                <div className="orc-statut-row">
                  <label>Statut</label>
                  <div className="orc-statut-btns">
                    {STATUTS.map(s => (
                      <button
                        key={s.value}
                        className={`orc-statut-btn ${editStatut === s.value ? 'active' : ''}`}
                        style={editStatut === s.value
                          ? { background: s.color, borderColor: s.color, color: '#fff' }
                          : { borderColor: s.color + '88', color: texteTheme(s.color) }}
                        onClick={() => setEditStatut(s.value)}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="orc-action-row">
                  <label>📅 Date de prochaine action</label>
                  <input
                    type="date"
                    className="orc-date-revision-input"
                    value={editDateRevision}
                    onChange={e => setEditDateRevision(e.target.value)}
                  />
                  <small className="orc-date-revision-hint">
                    Si renseignée, l'OR apparaît dans la vue "Tous les dossiers" uniquement jusqu'à cette date.
                  </small>
                </div>
                <button className="orc-btn-save" onClick={handleSaveStatut} disabled={saving}>
                  {saving ? 'Enregistrement…' : '✓ Enregistrer'}
                </button>
              </div>
            </div>

            {/* ── Commentaires ── */}
            <div className="orc-modal-section">
              <div className="orc-modal-section-title">Commentaires ({commentaires.length})</div>
              <div className="orc-comments">
                {commentaires.length === 0 && <div className="orc-no-comment">Aucun commentaire</div>}
                {commentaires.map(c => (
                  <div key={c.id} className="orc-comment">
                    <div className="orc-comment-header">
                      <span className="orc-comment-author">{c.user_label || 'Inconnu'}</span>
                      <span className="orc-comment-date">
                        {new Date(c.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </span>
                      {isAdmin && (
                        <button className="orc-comment-del" onClick={() => handleDeleteComment(c.id)} title="Supprimer">✕</button>
                      )}
                    </div>
                    <div className="orc-comment-body">{c.contenu}</div>
                  </div>
                ))}
                <div className="orc-comment-form">
                  <textarea
                    className="orc-comment-input"
                    rows={2}
                    placeholder="Ajouter un commentaire…"
                    value={newComment}
                    onChange={e => setNewComment(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddComment(); } }}
                  />
                  <button
                    className="orc-btn-comment"
                    onClick={handleAddComment}
                    disabled={savingComment || !newComment.trim()}
                  >
                    {savingComment ? '…' : 'Envoyer'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
